import {ImageService} from "esri/Features";
import {L} from "../../lt-ng/landcare-toolkit";
import {CustomLayers} from "../../services/custom-layer-service";
import {CustomLayerAPI} from "../../services/custom-layer-api";
import {FeatureLayer, GroupLayer} from "../../@types/esri/Features";
import {MapService} from "../../@types/esri/MapService";
import ICustomLayerService = CustomLayers.ICustomLayerService;
import CustomLayerType = CustomLayerAPI.CustomLayerType;
import LayerType = L.util.LayerType;
import esriServiceName = CustomLayerAPI.esriServiceName;
import esriServiceDescription = CustomLayerAPI.esriServiceDescription;
import projFromSpatialReference = CustomLayerAPI.projFromSpatialReference;
import {transformExtent} from "ol/proj";
import {intersects} from "ol/extent";
import {WMTSCapabilities} from "ol/format";
import {IToastService, TOAST_DURATION_SNACKBAR} from "../../services/ToastService";

export interface ICustomLayerSpec {
	url: string;
	layerType: CustomLayerType;
}

interface ICustomLayerScope extends ng.IScope {
	esriImageCapabilities?: ImageService|FeatureLayer|MapService|GroupLayer;
	theLayer: ICustomLayerSpec;
	createLayer();
	wmtsCapabilities: object;
	wmtsLayersChecked: object;
	validLayers: object[];
	wmtsLayersType: L.util.LayerType[];
	doAddSelectedLayers();
	leave();
	errorReasons: string[];
	errorMessage: string;
	urlError: boolean;
	allAreChecked() : boolean;
	anyAreChecked(): boolean;
	toggleSelectAll();
	boundsInsideMap(bounds : [number, number, number, number]): boolean;
	layerError(layer: any) : string;
	capabilitiesInputPlaceholder(): string;
	esriServiceName(): string;
	esriDescription();
	showExample($event: ng.IAngularEvent, layerType: CustomLayerType, url: string);
	selectedLayers: {
		allChecked: boolean;
	}
}

export class CustomLayerCtrl {
	public static $inject = [
		'$scope',
		'$http',
		'customLayerService',
		'toastService'
	];

	constructor(
		$scope: ICustomLayerScope,
		$http: ng.IHttpService,
		customLayerService: ICustomLayerService,
		toastService: IToastService) {

		let reset = () => {
			$scope.urlError = false;
			$scope.errorMessage = '';
			$scope.wmtsCapabilities = null;
			$scope.wmtsLayersType = [];
			$scope.errorReasons = [];
			$scope.validLayers = [];
			$scope.theLayer = {
				url: '',
				layerType: CustomLayerType.WMTS
			};
		};

		reset();

		let httpsError = false;
		$scope.$watch('theLayer.url', () => {
			let url = $scope.theLayer.url.toLowerCase();

			if (url.indexOf('http:') === 0) {
				$scope.errorMessage = `Unsupported protocol - the URL you've entered starts with <em>http:</em>. <br/>
					Our Environment is hosted using <a target="_blank" href="https://en.wikipedia.org/wiki/HTTPS">HTTPS</a>, 
					so it can only display a maps from URLs that start with <em>https:</em>`;
				httpsError = true;
				$scope.urlError = true;
			} else if (httpsError) {
				$scope.errorMessage = '';
				$scope.urlError = false;
				httpsError = false;
			}
		});

		$scope.leave = () => {
			reset();
			$scope.$emit('showLayerSelector');
		};

		function isResultWFS(result: any) {
			return result && result.ServiceIdentification && result.ServiceIdentification.ServiceType == 'WFS';
		}

		const onHttpError = (error) => {
			$scope.errorMessage = "Failed to retrieve capabilities document";
			console.log('Failed to get capabilities document', error);
		};

		$scope.capabilitiesInputPlaceholder = () => {
			switch ($scope.theLayer && $scope.theLayer.layerType) {
				case CustomLayerAPI.CustomLayerType.EsriImage:
					return 'Esri REST API URL';
				case CustomLayerAPI.CustomLayerType.WMTS:
					return 'WMTS Capabilities URL';
				default:
					return 'resource URL';
			}
		};

		const sortWmtsLayers = (a: { Title: string }, b: { Title: string }) => {
			const titleA = a.Title || '';
			const titleB = b.Title || '';
			return titleA.localeCompare(titleB);
		}

		function createWMTSLayer(layerSpec: ICustomLayerSpec) {
			$scope.errorMessage = '';
			return $http.get<string>(layerSpec.url,{
				cache: true
			}).then((response) => {
				let parser = new WMTSCapabilities();
				let result = null;
				try {
					 result = <any> parser.read(response.data);
				} catch (error) {
					$scope.errorMessage = 'Failed to parse capabilities document';
					return;
				}

				let layers = result && result.Contents && result.Contents.Layer;

				if (!layers) {
					if (isResultWFS(result)) {
						$scope.errorMessage = 'Unsupported layer type: WFS. WMTS is the only type of third party layer supported.'
					} else {
						$scope.errorMessage = 'Invalid WMTS capabilities document, no suitable layers found.';
					}
					return;
				}

				$scope.wmtsCapabilities = result;
				$scope.wmtsLayersChecked = {};

				let reasons = {};
				$scope.validLayers = layers.filter((l) => {
						let reason = $scope.layerError(l);
						if (reason) {
							reasons[reason] = true;
						}
						return !reason;
					})
					.sort(sortWmtsLayers);

				$scope.errorReasons = Object.keys(reasons);

				$scope.validLayers.forEach((l, i) => {
					$scope.wmtsLayersChecked[i] = true;
				});

				$scope.wmtsLayersType = layers.map(() => 'SCIENCE');

				$scope['backStack'].push(() => {
					$scope.errorMessage = '';
					$scope.wmtsCapabilities = null;
					$scope.wmtsLayersType = [];
					$scope.errorReasons = [];
					$scope.validLayers = [];
				});

				(<any> $scope.$parent).title = 'Pick your layers'

			}, onHttpError);
		}

		function createEsriImageLayer(layerSpec: ICustomLayerSpec) {
			$scope.errorMessage = '';

			const url = new URL(layerSpec.url);
			url.searchParams.set('f', 'json');

			return $http.get<ImageService>(url.toString(), {cache: true})
				.then(response => {
					const data = response.data;
					const spatialReference = data.spatialReference || (data.extent && data.extent.spatialReference);
					const projection = projFromSpatialReference(spatialReference);

					$scope['backStack'].push(() => {
						$scope.errorMessage = '';
						$scope.esriImageCapabilities = null;
						$scope.wmtsLayersType = [];
						$scope.errorReasons = [];
						$scope.validLayers = [];
					});

					if (!projection) {
						$scope.errorMessage = 'Our Environment cannot display this map because the projection is unsupported';
						return;
					}

					$scope.esriImageCapabilities = data;

					console.log(data);
				}, onHttpError);
		}

		$scope.esriServiceName = () => esriServiceName($scope.esriImageCapabilities);
		$scope.esriDescription = () => esriServiceDescription($scope.esriImageCapabilities);

		$scope.createLayer = () => {
			// add https:// if it is missing
			if ($scope.theLayer.url.indexOf('https://') !== 0) {
				$scope.theLayer.url = 'https://' + $scope.theLayer.url;
			}

			switch ($scope.theLayer.layerType) {
				case CustomLayerAPI.CustomLayerType.WMTS:
					createWMTSLayer($scope.theLayer);
					break;
				case CustomLayerAPI.CustomLayerType.EsriImage:
					createEsriImageLayer($scope.theLayer);
					break;
			}
		};

		const doAddSelectedWMTSLayers = () => {
			(<any> $scope.validLayers)
				.map((l, i) => [l, i])
				.filter((pair) => $scope.wmtsLayersChecked[pair[1]])
				.forEach((pair) => {
					let l = pair[0];
					let i = pair[1];

					customLayerService.createCustomLayer(
						{
							kind: CustomLayerType.WMTS,
							capabilitiesURL: $scope.theLayer.url,
							identifier: l.Identifier,
							layerType: $scope.wmtsLayersType[i],
							included: true,
							visible: false
						}
					).catch(reason => {
						console.warn(reason);
						toastService.showToast('Unable to parse some/all of the custom layers',
							TOAST_DURATION_SNACKBAR,
							undefined,
							undefined,
							'doAddSelectedWMTSLayers');
					})
				});
		};

		const doAddSelectedEsriImageLayer = () => {
			const layerType: LayerType = "SCIENCE";
			$scope.esriImageCapabilities = null;

			customLayerService.createCustomLayer(
				{
					kind: CustomLayerType.EsriImage,
					url: $scope.theLayer.url,
					included: true,
					visible: false,
					layerType
				}
			);
		};

		$scope.doAddSelectedLayers = () => {
			// let layerGroup = mapService.getCustomLayerGroup();
			switch($scope.theLayer.layerType) {
				case CustomLayerType.WMTS:
					doAddSelectedWMTSLayers();
					break;
				case CustomLayerAPI.CustomLayerType.EsriImage:
					doAddSelectedEsriImageLayer();
					break;
			}

			$scope.leave();
		};

		$scope.selectedLayers = {
			get allChecked() {
				return Object.values($scope.wmtsLayersChecked || {}).every(v => !!v);
			},
			set allChecked(val: boolean) {
				Object.keys($scope.wmtsLayersChecked || {}).forEach(k => {
					$scope.wmtsLayersChecked[k] = val;
				})
			}
		}

		$scope.allAreChecked = () => Object.values($scope.wmtsLayersChecked || {}).every(v => !!v);

		$scope.anyAreChecked = () => Object.values($scope.wmtsLayersChecked || {}).some(v => !!v);

		$scope.toggleSelectAll = () => {
			let keys = Object.keys($scope.wmtsLayersChecked);
			let someUnChecked = keys.some((k) => !$scope.wmtsLayersChecked[k]);

			keys.forEach((k) => {
				$scope.wmtsLayersChecked[k] = someUnChecked; // if some are 'un-checked' then 'all should be checked'
				// otherwise, un check all
			});
		};

		$scope.showExample = ($event: ng.IAngularEvent, layerType: CustomLayerType, url: string) => {
			if ($event) $event.preventDefault();

			$scope.theLayer = {
				layerType,
				url
			};

			return false;
		};

		$scope.layerError = (layer) => {
			let layerType = $scope.theLayer.layerType;
			let bounds = null;
			switch (layerType) {
				case CustomLayerType.WMTS:
					bounds = layer.WGS84BoundingBox;
					if (!layer['Style'] || !layer['Style'].length) {
						return 'style';
					}

					break;
			}
			if (!$scope.boundsInsideMap(bounds)) {
				return 'bounds'
			}

			return null;
		};

		$scope.boundsInsideMap = (bounds : [number, number, number, number]) => {
			let mapMaxExtent = L.util.Map.defaultExtent();

			if (mapMaxExtent && bounds) {
				let extentWGS84 = transformExtent(mapMaxExtent, L.util.Map.defaultProjection(), "EPSG:4326");
				return intersects(extentWGS84, bounds);
			}
			return true;
		}
	}
}